import { collection, updateDoc, deleteDoc, doc, getDocs, getDoc, query, orderBy, runTransaction, where } from "firebase/firestore";
import { db } from "./firebase";
import StorageService from "./storage_service";
import { memberToFirestore, memberDataFromFirestore } from "../models/member";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * MemberService
 * Centraliza as operações de banco de dados para o módulo de Clientes (Membros).
 * 
 * Todas as leituras passam por `memberDataFromFirestore` para garantir
 * que os dados retornados estejam no formato flat do formulário React:
 * - address: objeto { street, number, ... } → campos flat (zipCode, address=street, number, ...)  
 * - gender/maritalStatus/status/membershipStatus: { id, label } → string ID
 * - Timestamps → Date / ISO string
 */
const MemberService = {
    /**
     * Busca todos os clientes de um tenant.
     */
    getMembers: async (idTenant) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS),
                orderBy("fullName", "asc")
            );
            const querySnapshot = await getDocs(q);
            const members = [];
            querySnapshot.forEach((docSnap) => {
                members.push(memberDataFromFirestore(docSnap.id, docSnap.data()));
            });
            return members;
        } catch (error) {
            console.error("Erro ao listar membros:", error);
            throw error;
        }
    },

    /**
     * Busca apenas os clientes ativos de um tenant.
     * Útil para o quiosque e listagens rápidas.
     * Alterado para fazer o filtro em memória, pois registros antigos
     * podem ter o status armazenado como objeto (ex: { id: "active", label: "Ativo" }).
     */
    getActiveMembers: async (idTenant) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS)
            );
            const querySnapshot = await getDocs(q);
            const members = [];
            querySnapshot.forEach((docSnap) => {
                const data = memberDataFromFirestore(docSnap.id, docSnap.data());
                // Flatten string vs object structure is handled by memberDataFromFirestore.
                // Now we just check if it resolved to "active"
                if (data.status === 'active') {
                    members.push(data);
                }
            });
            // Ordena em memória para evitar necessidade de índice composto
            return members.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
        } catch (error) {
            console.error("Erro ao listar membros ativos:", error);
            throw error;
        }
    },

    getMemberById: async (idTenant, idMember) => {
        try {
            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
            const memberSnap = await getDoc(memberRef);
            if (!memberSnap.exists()) return null;
            return memberDataFromFirestore(memberSnap.id, memberSnap.data());
        } catch (error) {
            console.error("Erro ao buscar membro por ID:", error);
            throw error;
        }
    },

    /**
     * Busca um cliente pelo seu idMember (ID sequencial).
     */
    getMemberByMemberNumber: async (idTenant, idMember) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS),
                where("memberNumber", "==", idMember)
            );
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) return null;

            const docSnap = querySnapshot.docs[0];
            return memberDataFromFirestore(docSnap.id, docSnap.data());
        } catch (error) {
            console.error("Erro ao buscar membro pelo idMember:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza um cliente com ID sequencial (idMember).
     */
    save: async (formData, idTenant, authUser, existingMembers = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            if (formData.id) {
                // UPLOAD DE IMAGEM
                let finalPhotoUrl = formData.photoUrl;
                if (finalPhotoUrl && finalPhotoUrl.startsWith('data:image')) {
                    const path = `tenants/${idTenant}/members/${formData.id}/profile.jpg`;
                    finalPhotoUrl = await StorageService.uploadBase64Image(path, finalPhotoUrl);
                    formData.photoUrl = finalPhotoUrl;
                }

                // UPDATE
                const memberData = memberToFirestore(formData, idTenant, authUser);
                const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, formData.id);
                const oldMember = existingMembers.find(m => m.id === formData.id) || {};
                await updateDoc(memberRef, memberData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldMember, memberData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.MEMBERS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: memberData.fullName,
                    description: `Atualizou o cliente "${memberData.fullName}"`,
                    details: changes
                });

                return { id: formData.id, ...memberData };
            } else {
                // CREATE com Transação para ID Sequencial
                const counterRef = doc(db, "tenants", idTenant, COLLECTIONS.COUNTERS, "members");
                const membersCol = collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS);
                const newDocRef = doc(membersCol);

                let nextId = 1;
                let idMemberStr = "0001";

                // UPLOAD DE IMAGEM (FEITO FORA DA TRANSAÇÃO PARA EVITAR RETENTATIVAS DUPLICADAS)
                let finalPhotoUrl = formData.photoUrl;
                if (finalPhotoUrl && finalPhotoUrl.startsWith('data:image')) {
                    const path = `tenants/${idTenant}/members/${newDocRef.id}/profile.jpg`;
                    finalPhotoUrl = await StorageService.uploadBase64Image(path, finalPhotoUrl);
                    formData.photoUrl = finalPhotoUrl;
                }

                const savedMember = await runTransaction(db, async (transaction) => {
                    const counterSnap = await transaction.get(counterRef);

                    if (counterSnap.exists()) {
                        nextId = (counterSnap.data().lastId || 0) + 1;
                    }

                    idMemberStr = String(nextId).padStart(4, "0");

                    const memberDataWithId = {
                        ...memberToFirestore(formData, idTenant, authUser),
                        memberNumber: idMemberStr
                    };

                    transaction.set(newDocRef, memberDataWithId);
                    transaction.set(counterRef, { lastId: nextId }, { merge: true });

                    return { id: newDocRef.id, ...memberDataWithId };
                });

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.MEMBERS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: savedMember.id,
                    entityName: savedMember.fullName,
                    description: `Criou o cliente "${savedMember.fullName}" (ID: ${idMemberStr})`
                });

                return savedMember;
            }
        } catch (error) {
            console.error("Erro no MemberService.save:", error);
            throw error;
        }
    },

    /**
     * Remove um cliente.
     */
    delete: async (memberId, idTenant, authUser, memberName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, memberId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: memberId,
                entityName: memberName,
                description: `Excluiu o cliente "${memberName || memberId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no MemberService.delete:", error);
            throw error;
        }
    }
};

export default MemberService;
